import { useAppKitProvider } from '@reown/appkit/react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { BrowserProvider, Eip1193Provider, ethers } from 'ethers';
import { bondingAbi } from 'src/contracts/abi';
import { ADDRESSES } from 'src/contracts/addresses';
import { useBalanceOf } from 'src/hooks/useBalanceOf';

const getCurrentState = async (walletProvider?: Eip1193Provider, tempTokenAddress?: string) => {
  if (!walletProvider || !tempTokenAddress) return;

  const provider = new BrowserProvider(walletProvider);
  const signer = await provider.getSigner();
  const bonding = new ethers.Contract(ADDRESSES.bondingAddress, bondingAbi, signer);
  const tokenInfo = await bonding.tokenInfo(tempTokenAddress);
  const agentToken = tokenInfo[3];

  return !!agentToken;
};

const unwrapToken = async (walletProvider?: Eip1193Provider, tempTokenAddress?: string) => {
  if (!walletProvider || !tempTokenAddress) return;

  const provider = new BrowserProvider(walletProvider);
  const signer = await provider.getSigner();
  const bonding = new ethers.Contract(ADDRESSES.bondingAddress, bondingAbi, signer);

  const tx = await bonding.unwrapToken(tempTokenAddress);
  await tx.wait();
};

export const useUnwrapToken = (tempTokenAddress?: string) => {
  const { walletProvider } = useAppKitProvider<Eip1193Provider>('eip155');
  const { balance } = useBalanceOf(tempTokenAddress || '');

  const { data: tempTokenState } = useQuery({
    queryKey: ['checkAgentToken'],
    queryFn: () => getCurrentState(walletProvider, tempTokenAddress),
    refetchOnWindowFocus: false,
    enabled: !!tempTokenAddress && !!walletProvider,
  });

  const { mutateAsync: unwrap, isPending: isPendingUnwrap } = useMutation({
    mutationKey: ['unwrapToken'],
    mutationFn: async () => {
      await unwrapToken(walletProvider, tempTokenAddress);
    },
    onError: (error) => {
      console.error('Error unwrapping token:', error);
      throw error;
    },
  });

  return {
    unwrap,
    isPendingUnwrap,
    isNeedToUnwrap: !!tempTokenState && balance?.formatted !== '0',
  };
};
